import { isDefined } from "@clipboard-health/util-ts";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { SUPPORT_WEBFORM_URL } from "@src/appV2/ContactCBHInfo/constants";
import { useGetFacilityProfile } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { ExternalLink } from "@src/appV2/lib/Links/ExternalLink";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useCreateWorkplaceAgreement } from "@src/appV2/WorkplaceAgreement/api/useCreateWorkplaceAgreement";
import { useGetWorkplaceAgreements } from "@src/appV2/WorkplaceAgreement/api/useGetWorkplaceAgreements";
import { WorkplaceAgreementPerUserDialog } from "@src/appV2/WorkplaceAgreement/WorkplaceAgreementPerUserDialog";
import { LoadingButton, Text } from "@src/lib/ui-react";
import { isEmpty } from "lodash";
import { useEffect, useRef, useState } from "react";

/**
 * Dispatches on the `REQUIRE_WORKPLACE_AGREEMENT` flag variation, which LaunchDarkly targets per
 * workplace (`workplaceId` / `parentFacilityId`). Each variation is its own component so its hooks
 * and API calls run only when that variation is active.
 */
export function WorkplaceAgreementDialog() {
  const { variation } = useCbhFlag(CbhFeatureFlag.REQUIRE_WORKPLACE_AGREEMENT, {
    defaultValue: {
      variation: "off",
      flexAgreementUrl: "",
      hiringAgreementUrl: "",
    },
  });

  if (variation === "per-user") {
    return <WorkplaceAgreementPerUserDialog />;
  }

  return <WorkplaceAgreementOnDialog />;
}

function WorkplaceAgreementOnDialog() {
  const session = useSession();
  const { admin: isAdmin, profile, user } = session ?? { profile: undefined, user: undefined };
  const workplaceId = profile?.userId;
  const userId = user?._id ?? "";

  const { variation, flexAgreementUrl, hiringAgreementUrl } = useCbhFlag(
    CbhFeatureFlag.REQUIRE_WORKPLACE_AGREEMENT,
    {
      defaultValue: {
        variation: "off",
        flexAgreementUrl: "",
        hiringAgreementUrl: "",
      },
    }
  );

  const requireWorkplaceAgreement =
    isDefined(workplaceId) && !isAdmin && !isEmpty(userId) && variation === "on";
  const {
    data: workplaceAgreements,
    isLoading: isLoadingWorkplaceAgreements,
    isError: isErrorGettingWorkplaceAgreements,
  } = useGetWorkplaceAgreements(workplaceId, undefined, {
    enabled: requireWorkplaceAgreement,
  });
  // Gate the dialog on the first shift post (populated async by the
  // ShiftCreated SQS consumer; shift-create mutations invalidate this query).
  // The string projection is SPACE-delimited (the facility-profile controller splits on " ");
  // a comma would be parsed as a single bogus field name and 400.
  const {
    data: facilityProfile,
    isLoading: isLoadingFacilityProfile,
    isError: isErrorGettingFacilityProfile,
  } = useGetFacilityProfile(workplaceId ?? "", "firstShiftCreatedAt legalEntityName", {
    enabled: requireWorkplaceAgreement,
  });
  const { mutateAsync: createWorkplaceAgreement, isLoading: isLoadingCreateWorkplaceAgreement } =
    useCreateWorkplaceAgreement(workplaceId);

  const [visible, setVisible] = useState<boolean>(true);

  // Prefill the editable legal entity name from the facility profile once it loads, until the
  // user edits it. The component stays mounted (returning null) while loading, so seed via effect.
  const [legalEntityName, setLegalEntityName] = useState<string>("");
  const [hasUserEditedLegalEntityName, setHasUserEditedLegalEntityName] = useState<boolean>(false);
  const [isEditingLegalEntityName, setIsEditingLegalEntityName] = useState<boolean>(false);
  useEffect(() => {
    if (!hasUserEditedLegalEntityName && isDefined(facilityProfile?.legalEntityName)) {
      setLegalEntityName(facilityProfile.legalEntityName);
    }
  }, [facilityProfile?.legalEntityName, hasUserEditedLegalEntityName]);

  // Focus the field when the user reveals it via "Edit" (avoids the static `autoFocus` attribute,
  // which jsx-a11y flags for unconditionally stealing focus).
  const legalEntityNameInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditingLegalEntityName) {
      legalEntityNameInputRef.current?.focus();
    }
  }, [isEditingLegalEntityName]);

  const trimmedLegalEntityName = legalEntityName.trim();
  const isLegalEntityNameValid = !isEmpty(trimmedLegalEntityName);

  // A backfilled legal entity name is treated as authoritative: show it read-only and require a
  // deliberate "Edit" to change it, reducing accidental edits/typos on a signed legal document.
  // When the profile has no value to confirm, fall back to the required input.
  const hasPrefilledLegalEntityName = !isEmpty(facilityProfile?.legalEntityName?.trim() ?? "");
  const isEditingOrEmptyLegalEntityName = isEditingLegalEntityName || !hasPrefilledLegalEntityName;

  const onAgree = async () => {
    try {
      await createWorkplaceAgreement({
        workplaceId,
        acceptedByUserId: userId,
        acceptedAt: new Date(),
        arbitrationAgreementVersionNumber: 1,
        legalEntityName: trimmedLegalEntityName,
      });
      setVisible(false);
    } catch {
      showErrorToast("Failed to accept agreement. Please try again or contact support.");
    }
  };

  if (!requireWorkplaceAgreement) {
    return null;
  }

  if (
    isErrorGettingWorkplaceAgreements ||
    isLoadingWorkplaceAgreements ||
    !isEmpty(workplaceAgreements?.data)
  ) {
    return null;
  }

  if (
    isErrorGettingFacilityProfile ||
    isLoadingFacilityProfile ||
    !isDefined(facilityProfile?.firstShiftCreatedAt)
  ) {
    return null;
  }

  return (
    <Dialog disableEscapeKeyDown fullWidth open={visible} maxWidth="sm">
      <DialogTitle sx={{ textAlign: "center" }}>Marketplace Access Agreements</DialogTitle>
      <DialogContent>
        <Stack spacing={3} alignItems="center" textAlign="center" sx={{ pt: 2 }}>
          <TextField
            fullWidth
            inputRef={legalEntityNameInputRef}
            label="Legal entity name"
            value={legalEntityName}
            onChange={(event) => {
              setHasUserEditedLegalEntityName(true);
              setLegalEntityName(event.target.value);
            }}
            error={isEditingOrEmptyLegalEntityName && !isLegalEntityNameValid}
            helperText={
              isEditingOrEmptyLegalEntityName && !isLegalEntityNameValid
                ? "Legal entity name is required to accept."
                : undefined
            }
            InputProps={{
              readOnly: !isEditingOrEmptyLegalEntityName,
              endAdornment:
                hasPrefilledLegalEntityName && !isEditingLegalEntityName ? (
                  <InputAdornment position="end">
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                      onClick={() => {
                        setIsEditingLegalEntityName(true);
                      }}
                      sx={{ flexShrink: 0 }}
                    >
                      Edit
                    </Button>
                  </InputAdornment>
                ) : undefined,
            }}
          />
          <Text variant="body1">
            Please carefully read our{" "}
            <ExternalLink
              to="https://www.clipboardhealth.com/terms-of-service?menu=none"
              underline="always"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
            >
              Terms of Service
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </ExternalLink>{" "}
            and Marketplace Access Agreements. By clicking &apos;AGREE AND ACCEPT,&apos; you
            represent and warrant that you have the authority to bind the organization associated
            with this account, that you reviewed and confirmed the legal entity name above as
            accurate, and that you have read, understood, and agree to be bound by the Terms of
            Service and Marketplace Access Agreements listed below.
          </Text>
          <Stack spacing={1} alignItems="center" textAlign="center">
            <ExternalLink
              to={flexAgreementUrl}
              underline="always"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
            >
              Flex Marketplace Access Agreement
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </ExternalLink>
            {!isEmpty(hiringAgreementUrl) ? (
              <ExternalLink
                to={hiringAgreementUrl}
                underline="always"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
              >
                Hiring Marketplace Access Agreement
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </ExternalLink>
            ) : null}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
        <Stack spacing={1} alignItems="center" textAlign="center">
          <LoadingButton
            variant="contained"
            size="large"
            sx={{ textTransform: "uppercase", px: 4, borderRadius: 6 }}
            disabled={isLoadingCreateWorkplaceAgreement || !isLegalEntityNameValid}
            isLoading={isLoadingCreateWorkplaceAgreement}
            onClick={onAgree}
          >
            AGREE AND ACCEPT
          </LoadingButton>
          <Text variant="body2">
            <ExternalLink to={SUPPORT_WEBFORM_URL} underline="hover">
              Contact Support
            </ExternalLink>
          </Text>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
